import type {ReactElement} from 'react';
import {useRef, useState} from 'react';
import TooltipTrigger from '@Components/tooltip-trigger/tooltip-trigger';
import {ClickableDiv} from '@Components/clickable-div';
import {TOOLTIP_POSITION} from '@Components/tooltip';
import type {PmwLogoProps} from './pmw-logo.types';
import styles from './pmw-logo.module.scss';

const getHoverMessages = (): string[] => {
  return [
    window.i18next.t('pmwjs_pmw_logo_hover_1'),
    window.i18next.t('pmwjs_pmw_logo_hover_2'),
    window.i18next.t('pmwjs_pmw_logo_hover_3'),
  ];
};

export function PmwLogo({className = '', wrapperClassName = '', animated = false, onClick}: PmwLogoProps): ReactElement {
  const rootClass = `${styles.logo} ${animated ? styles.animated : ''} ${className}`.trim();
  const wrapperClass = `${styles.logoWrapper} ${wrapperClassName}`.trim();
  const hoverMessageIndex = useRef(0);
  const [tooltipText, setTooltipText] = useState<string>(getHoverMessages()[0]);

  const handleMouseEnter = (): void => {
    if (animated) {
      return;
    }

    const messages = getHoverMessages();
    setTooltipText(messages[hoverMessageIndex.current % messages.length]);
    hoverMessageIndex.current += 1;
  };

  const getLogoSvg = (): ReactElement => {
    return (
      <svg className={rootClass} xmlns="http://www.w3.org/2000/svg" width="21" height="26" viewBox="0 0 21 26" fill="none">
        <path className={styles.wingLower} d="M11.9082 12.248L5.63136 24.8038C5.5554 24.9538 5.52715 25.1235 5.5504 25.2901C5.57365 25.4567 5.64729 25.6121 5.76144 25.7356C5.87559 25.8591 6.02478 25.9447 6.189 25.981C6.35322 26.0173 6.5246 26.0024 6.68014 25.9385L20.2576 20.4052L11.9082 12.248Z" fill="#007CA8"/>
        <path className={styles.shadow} d="M10.332 15.4055L12.6099 10.8398L20.259 20.4022L10.332 15.4055Z" fill="#2C2B44"/>
        <path className={styles.wingUpper} d="M20.2591 20.4024L18.6519 9.45421C18.5328 8.64299 18.1996 7.87817 17.6868 7.23841C17.174 6.59866 16.5 6.10709 15.7341 5.81426L0.603753 0.0289328C0.508053 -0.00733681 0.402788 -0.00957291 0.305634 0.0226007C0.20848 0.0547744 0.125357 0.119397 0.0702217 0.205618C0.015086 0.291839 -0.00870274 0.394406 0.00285005 0.496094C0.0144028 0.597782 0.0605936 0.6924 0.133665 0.764055L20.2591 20.4024Z" fill="#3FBCE8"/>
      </svg>
    );
  };

  const getContent = (): ReactElement => {
    if (animated) {
      return getLogoSvg();
    }

    return (
      <TooltipTrigger text={tooltipText} position={TOOLTIP_POSITION.RIGHT_MIDDLE} tooltipClass={styles.tooltipRight}>
        {getLogoSvg()}
      </TooltipTrigger>
    );
  };

  const wrap = (content: ReactElement): ReactElement => {
    if (onClick) {
      return (
        <ClickableDiv onClick={onClick} onMouseEnter={handleMouseEnter} className={`${wrapperClass} _cursor-pointer`}>
          {content}
        </ClickableDiv>
      );
    }

    return (
      <div onMouseEnter={handleMouseEnter} className={wrapperClass}>
        {content}
      </div>
    );
  };

  return wrap(getContent());
}
